import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import CloseIcon from "@mui/icons-material/Close";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { Button, MenuItem, TextField } from "@mui/material";
// import MrcSegregationMonthSelect, {
//   useMrcSegregationMonth,
// } from "./MrcSegregationMonthSelect";
import {
  MRC_DOCUMENT_VERIFIED_REMARK,
  calcUnitsAdjusted,
  calcUnitsChargedRevised,
  formatDisplayValue,
  formatUnitsAdjDisplay,
  formatUnitsChargedDisplay,
  formatUnitsRevisedDisplay,
  getMrcEnteredReadingError,
  getMrcEvidenceRemarksLabel,
  getMrcEvidenceRemarksVariant,
  applyMrcEvidenceRemarksMode,
  hasConsumerClaimedReading,
  hasMrcPresentReading,
  isMdiMeterRow,
  mapDocumentsToReadingRows,
  sanitizeReadingInput,
  toNumberOrNull,
} from "./mrcReadingUtils";
import { getDocumentBackGroundColor, handleFileUpload } from "../../../services/Functions";
import OpenBase64ImageInPopup from "./MrcOpenBase64ImageInPopup";
import crossIcon from "../../../images/remove.png";
import { documentExtensions } from "../../../utils/PortalName";
import MrcEvidencePreview from "./MrcEvidencePreview";
import "./MeterReadingCorrectionForm.scss";

const sortReadingRows = (rows) =>
  [...rows].sort((a, b) => {
    const setDiff = Number(a.setNo) - Number(b.setNo);
    if (setDiff !== 0) return setDiff;
    return Number(a.meterId) - Number(b.meterId);
  });

const statusLabel = (flag, hasImage) => {
  if (!hasImage) return "N/A";
  if (flag === 1) return "Accepted";
  if (flag === -1) return "Rejected";
  return "Pending";
};

/** Authority Approved Reading required where consumer claimed a reading. */
const requiresAuthorityClaimed = (row) => hasConsumerClaimedReading(row);

/** Site Survey Reading required where consumer claimed a reading. */
const requiresSiteSurvey = (row) => hasConsumerClaimedReading(row);

const hasSiteSurveyReadingEntered = (row, readings) =>
  String(readings[row.id] ?? "").trim() !== "";

/**
 * Meter reading grid for Document / Authority / Site Survey
 * (full form columns, data from getMRCDocuments).
 */
const MrcDocumentsReadingTable = forwardRef(
  (
    {
      documents = [],
      loading = false,
      applicationProgressId,
      objectionStatus,
      onUpdateImageFlag,
      showAuthorityClaimed = false,
      showSiteSurvey = false,
      readOnly = false,
      /** History: Application Submitted — show evidence image only, no Accepted/Pending label */
      hideEvidenceStatus = false,
      /** auto | reupload | none — history/reupload context for preview remarks */
      evidenceRemarksMode = "auto",
      onAuthorityClaimedChange,
      onValidationError,
      meterPhaseType = "",
    },
    ref
  ) => {
    const rows = sortReadingRows(
      mapDocumentsToReadingRows(documents, { meterPhaseType })
    );
    // const {
    //   value: segregationMonth,
    //   onChange: onSegregationMonthChange,
    //   disabled: segregationMonthDisabled,
    // } = useMrcSegregationMonth();
    // Authority Verification (2), Forward for Billing (4), Post for Billing (5).
    // const progressId = Number(applicationProgressId);
    // const showSegregationMonth =
    //   progressId === 2 || progressId === 4 || progressId === 5;
    const [activeRow, setActiveRow] = useState(null);
    const [authorityReadings, setAuthorityReadings] = useState({});
    const [authorityErrors, setAuthorityErrors] = useState({});
    const [siteSurveyReadings, setSiteSurveyReadings] = useState({});
    const [siteSurveyErrors, setSiteSurveyErrors] = useState({});
    const [siteImages, setSiteImages] = useState({});
    const [siteImageErrors, setSiteImageErrors] = useState({});
    const [previewSiteImage, setPreviewSiteImage] = useState(null);

    // Forward for Billing after survey: site survey read-only + authority editable
    const siteSurveyReadOnly = Boolean(showSiteSurvey && showAuthorityClaimed);
    const getRemarksRow = (row) =>
      applyMrcEvidenceRemarksMode(row, evidenceRemarksMode);

    useEffect(() => {
      if (!showAuthorityClaimed) return;
      setAuthorityReadings((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (next[row.id] === undefined) {
            // Forward for Billing after survey (editable): start blank (user must enter)
            // Post for Billing / other readOnly views: show saved authority reading
            // API default 0 is treated as unset in mapDocumentsToReadingRows
            if (siteSurveyReadOnly && !readOnly) {
              next[row.id] = "";
              return;
            }
            next[row.id] =
              row.authorityClaimed != null
                ? String(row.authorityClaimed)
                : "";
          }
        });
        return next;
      });
    }, [documents, showAuthorityClaimed, siteSurveyReadOnly, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
      if (!showSiteSurvey) return;
      // Editable Site Survey: always start blank (user must enter fresh readings/images).
      // Prefill only on read-only views (Forward / Post for Billing) from saved API data.
      setSiteSurveyReadings((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (next[row.id] === undefined) {
            if (siteSurveyReadOnly || readOnly) {
              next[row.id] =
                row.siteSurveyReading != null
                  ? String(row.siteSurveyReading)
                  : "";
            } else {
              next[row.id] = "";
            }
          }
        });
        return next;
      });
      setSiteImages((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (next[row.id] !== undefined) return;
          if ((siteSurveyReadOnly || readOnly) && row.siteSurveyImageSrc) {
            next[row.id] = {
              file: row.siteSurveyImageSrc,
              thumbnail: row.siteSurveyImageSrc,
            };
          }
        });
        return next;
      });
    }, [documents, showSiteSurvey, siteSurveyReadOnly, readOnly]); // eslint-disable-line react-hooks/exhaustive-deps

    const hideImagePopup = () => setActiveRow(null);

    const handleUpdateDocFlag = (titleToUpdateFlag, value, remarks) => {
      if (typeof onUpdateImageFlag === "function") {
        onUpdateImageFlag(titleToUpdateFlag, value, remarks);
      }
      if (value === 1 || value === -1) {
        setActiveRow(null);
      }
    };

    const hasSiteSurveyValueForRow = (row) => {
      const fromState = String(siteSurveyReadings[row.id] ?? "").trim();
      if (fromState !== "" && fromState !== "0") return true;
      const fromDoc = toNumberOrNull(row.siteSurveyReading);
      return fromDoc != null && fromDoc !== 0;
    };

    const parseAuthoritySelectReading = (raw) => {
      const s = String(raw ?? "").trim();
      if (!s) return null;
      if (s.startsWith("claimed:") || s.startsWith("survey:")) {
        return toNumberOrNull(s.slice(s.indexOf(":") + 1));
      }
      return toNumberOrNull(s);
    };

    const getAuthoritySelectOptions = (row) => {
      const options = [];

      const pushOption = (raw, source, labelPrefix) => {
        const n = toNumberOrNull(raw);
        if (n == null || n === 0) return;
        options.push({
          value: `${source}:${n}`,
          reading: String(n),
          label: `${labelPrefix}: ${formatDisplayValue(n)}`,
        });
      };

      if (hasConsumerClaimedReading(row)) {
        pushOption(row.claimed, "claimed", "User Claimed");
      }
      if (hasSiteSurveyValueForRow(row)) {
        pushOption(
          siteSurveyReadings[row.id] ?? row.siteSurveyReading,
          "survey",
          "Site Survey"
        );
      }

      return options;
    };

    const isAuthorityRequiredForRow = (row) => {
      if (isMdiMeterRow(row)) return false;
      // Forward for Billing after survey: only where User Claimed or Site Survey reading exists
      if (siteSurveyReadOnly) {
        return (
          hasConsumerClaimedReading(row) || hasSiteSurveyValueForRow(row)
        );
      }
      return requiresAuthorityClaimed(row);
    };

    const getAuthorityClaimedError = (row, readings = authorityReadings) => {
      if (isMdiMeterRow(row)) return "";
      const required = isAuthorityRequiredForRow(row);
      const raw = String(readings[row.id] ?? "").trim();
      if (!required && !raw) return "";
      if (!raw) {
        return "Required";
      }

      const approved = siteSurveyReadOnly
        ? parseAuthoritySelectReading(raw)
        : toNumberOrNull(raw);
      if (approved === null) {
        return "Authority Approved Reading must be numeric.";
      }

      if (siteSurveyReadOnly) {
        const allowed = getAuthoritySelectOptions(row).map((o) => o.value);
        if (!allowed.includes(raw)) {
          return "Select User Claimed or Site Survey reading.";
        }
      }

      return getMrcEnteredReadingError(
        approved,
        row,
        "Authority Approved Reading"
      );
    };

    const emitValidationError = (
      authorityMap = authorityErrors,
      siteMap = siteSurveyErrors,
      imageMap = siteImageErrors
    ) => {
      if (typeof onValidationError !== "function") return;
      const first =
        Object.values(authorityMap || {}).find(Boolean) ||
        Object.values(siteMap || {}).find(Boolean) ||
        Object.values(imageMap || {}).find(Boolean) ||
        "";
      onValidationError(first);
    };

    const hasSiteImageForRow = (row, images = siteImages) => {
      const entry = images[row.id];
      return Boolean(entry?.thumbnail || entry?.file);
    };

    const isSiteSurveyReadingRequired = (row, images = siteImages) =>
      !isMdiMeterRow(row) &&
      (requiresSiteSurvey(row) || hasSiteImageForRow(row, images));

    const getSiteSurveyReadingError = (
      row,
      readings = siteSurveyReadings,
      images = siteImages
    ) => {
      if (isMdiMeterRow(row)) return "";
      const required = isSiteSurveyReadingRequired(row, images);
      const entered = hasSiteSurveyReadingEntered(row, readings);
      if (!required && !entered) return "";

      const raw = String(readings[row.id] ?? "").trim();
      if (!raw) return "Required";

      const survey = toNumberOrNull(raw);
      if (survey === null) {
        return "Site Survey Reading must be numeric.";
      }

      return getMrcEnteredReadingError(survey, row, "Site Survey Reading");
    };

    const getSiteImageError = (
      row,
      images = siteImages,
      readings = siteSurveyReadings
    ) => {
      if (isMdiMeterRow(row)) return "";
      const needsImage =
        requiresSiteSurvey(row) || hasSiteSurveyReadingEntered(row, readings);
      if (!needsImage) return "";
      const entry = images[row.id];
      if (entry?.thumbnail || entry?.file) return "";
      return "Upload site evidence.";
    };

    const validateAuthorityClaimed = () => {
      if (!showAuthorityClaimed) return true;
      const errors = {};
      let valid = true;
      rows.forEach((row) => {
        const message = getAuthorityClaimedError(row);
        if (message) {
          errors[row.id] = message;
          valid = false;
        }
      });
      setAuthorityErrors(errors);
      emitValidationError(errors, siteSurveyErrors, siteImageErrors);
      return valid;
    };

    const validateSiteSurvey = () => {
      if (!showSiteSurvey || siteSurveyReadOnly) return true;
      const readingErrors = {};
      const imageErrors = {};
      let valid = true;
      rows.forEach((row) => {
        const readingMessage = getSiteSurveyReadingError(row);
        if (readingMessage) {
          readingErrors[row.id] = readingMessage;
          valid = false;
        }
        const imageMessage = getSiteImageError(row);
        if (imageMessage) {
          imageErrors[row.id] = imageMessage;
          valid = false;
        }
      });
      setSiteSurveyErrors(readingErrors);
      setSiteImageErrors(imageErrors);
      emitValidationError(authorityErrors, readingErrors, imageErrors);
      return valid;
    };

    const handleAuthorityClaimedChange = (rowId, value) => {
      // Dropdown values are claimed:123 / survey:123 — keep prefix.
      // Free-text authority input: digits only.
      const cleaned = siteSurveyReadOnly
        ? String(value ?? "")
        : sanitizeReadingInput(value);
      setAuthorityReadings((prev) => {
        const next = { ...prev, [rowId]: cleaned };
        if (typeof onAuthorityClaimedChange === "function") {
          onAuthorityClaimedChange(next);
        }
        const row = rows.find((r) => r.id === rowId);
        if (row) {
          const message = getAuthorityClaimedError(row, next);
          setAuthorityErrors((prevErrors) => {
            const nextErrors = { ...prevErrors };
            if (message) nextErrors[rowId] = message;
            else delete nextErrors[rowId];
            emitValidationError(nextErrors, siteSurveyErrors, siteImageErrors);
            return nextErrors;
          });
        }
        return next;
      });
    };

    const handleSiteSurveyReadingChange = (rowId, value) => {
      const cleaned = sanitizeReadingInput(value);
      setSiteSurveyReadings((prev) => {
        const next = { ...prev, [rowId]: cleaned };
        const row = rows.find((r) => r.id === rowId);
        if (row) {
          const message = getSiteSurveyReadingError(row, next, siteImages);
          let nextReadingErrors = siteSurveyErrors;
          setSiteSurveyErrors((prevErrors) => {
            nextReadingErrors = { ...prevErrors };
            if (message) nextReadingErrors[rowId] = message;
            else delete nextReadingErrors[rowId];
            return nextReadingErrors;
          });
          const imageMessage = getSiteImageError(row, siteImages, next);
          setSiteImageErrors((prevErrors) => {
            const nextErrors = { ...prevErrors };
            if (imageMessage) nextErrors[rowId] = imageMessage;
            else delete nextErrors[rowId];
            emitValidationError(authorityErrors, nextReadingErrors, nextErrors);
            return nextErrors;
          });
        }
        return next;
      });
    };

    const handleSiteImageUpload = (rowId, event) => {
      const file = event?.target?.files?.[0];
      if (!file) return;

      const newFileName = `siteSurvey-set-${rowId}.${file.name.split(".").pop()}`;

      const setFile = (val) =>
        setSiteImages((prev) => ({
          ...prev,
          [rowId]: {
            ...(prev[rowId] || {}),
            file: val,
            // Keep previous thumbnail until new one is ready so layout doesn't jump.
            thumbnail: prev[rowId]?.thumbnail || "",
          },
        }));
      const setErr = (err) =>
        setSiteImageErrors((prev) => {
          const next = { ...prev };
          if (err) next[rowId] = err;
          else delete next[rowId];
          emitValidationError(authorityErrors, siteSurveyErrors, next);
          return next;
        });
      const setThumb = (thumb) => {
        setSiteImages((prev) => {
          const nextImages = {
            ...prev,
            [rowId]: {
              file: prev[rowId]?.file || "",
              thumbnail: thumb || "",
            },
          };
          const row = rows.find((r) => r.id === rowId);
          if (row) {
            const readingMessage = getSiteSurveyReadingError(
              row,
              siteSurveyReadings,
              nextImages
            );
            setSiteSurveyErrors((prevErrors) => {
              const nextErrors = { ...prevErrors };
              if (readingMessage) nextErrors[rowId] = readingMessage;
              else delete nextErrors[rowId];
              return nextErrors;
            });
          }
          return nextImages;
        });
        setSiteImageErrors((prev) => {
          const next = { ...prev };
          delete next[rowId];
          emitValidationError(authorityErrors, siteSurveyErrors, next);
          return next;
        });
      };

      handleFileUpload(event, () => {}, setFile, setErr, setThumb, newFileName);
      // Allow selecting the same file again after clear/reupload.
      event.target.value = "";
    };

    const clearSiteImage = (rowId) => {
      setSiteImages((prev) => {
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
      const row = rows.find((r) => r.id === rowId);
      const message = row
        ? getSiteImageError(row, {}, siteSurveyReadings)
        : "Upload site evidence.";
      setSiteImageErrors((prev) => {
        const next = { ...prev };
        if (message) next[rowId] = message;
        else delete next[rowId];
        return next;
      });
      if (row) {
        const readingMessage = getSiteSurveyReadingError(
          row,
          siteSurveyReadings,
          {}
        );
        setSiteSurveyErrors((prevErrors) => {
          const nextErrors = { ...prevErrors };
          if (readingMessage) nextErrors[rowId] = readingMessage;
          else delete nextErrors[rowId];
          const nextImages = { ...siteImageErrors };
          if (message) nextImages[rowId] = message;
          else delete nextImages[rowId];
          emitValidationError(authorityErrors, nextErrors, nextImages);
          return nextErrors;
        });
      }
    };

    const getSiteImagePreviewSrc = (entry) => {
      if (!entry) return "";
      if (entry.thumbnail && String(entry.thumbnail).startsWith("data:")) {
        return entry.thumbnail;
      }
      return "";
    };

    useImperativeHandle(ref, () => ({
      validateAuthorityClaimed,
      getAuthorityReadings: () => {
        const out = {};
        Object.entries(authorityReadings).forEach(([id, raw]) => {
          const n = parseAuthoritySelectReading(raw);
          out[id] = n == null ? "" : String(n);
        });
        return out;
      },
      validateSiteSurvey,
      getSiteSurveyReadings: () => ({ ...siteSurveyReadings }),
      getSiteSurveyImages: () => {
        const out = {};
        Object.keys(siteImages).forEach((id) => {
          out[id] = siteImages[id]?.thumbnail || siteImages[id]?.file || "";
        });
        return out;
      },
    }));

    const segregationMonthField = null;
    // const segregationMonthField = showSegregationMonth ? (
    //   <div className="mrc-segregation-month-toolbar">
    //     <MrcSegregationMonthSelect
    //       disabled={segregationMonthDisabled}
    //       value={segregationMonth}
    //       onChange={onSegregationMonthChange}
    //       className="mrc-segregation-month-field--toolbar"
    //     />
    //   </div>
    // ) : null;

    if (loading) {
      return (
        <div className="mrc-documents-reading-table">
          {segregationMonthField}
          <p className="mrc-meter-hint">Loading reading information…</p>
        </div>
      );
    }

    if (!rows.length) {
      return (
        <div className="mrc-documents-reading-table">
          {segregationMonthField}
          <p className="mrc-meter-hint">No meter reading documents found.</p>
        </div>
      );
    }

    // Consumer claimed: always on Forward/Post for Billing (incl. after site survey).
    // Hidden only on pure Site Survey step where survey reading replaces it.
    const showClaimedColumn = !showSiteSurvey || showAuthorityClaimed;
    const showAuthorityColumn = showAuthorityClaimed;
    // Claimed evidence + site evidence both on Forward for Billing after survey.
    const showEvidenceColumn = !showSiteSurvey || showAuthorityClaimed;
    const evidenceViewOnly = readOnly || siteSurveyReadOnly;

    return (
      <div
        className={`mrc-documents-reading-table mrc-documents-reading-table--full${
          showAuthorityClaimed ? " mrc-documents-reading-table--authority" : ""
        }${showSiteSurvey ? " mrc-documents-reading-table--site-survey" : ""}${
          siteSurveyReadOnly ? " mrc-documents-reading-table--billing-survey" : ""
        }`}
      >
        {segregationMonthField}
        <div className="mrc-meter-table">
          <div className="mrc-meter-table__head">
            <table>
              <colgroup>
                <col className="col-set" />
                <col className="col-meter" />
                <col className="col-serial" />
                <col className="col-mf" />
                <col className="col-num" />
                <col className="col-num" />
                <col className="col-num" />
                {showClaimedColumn ? <col className="col-claimed" /> : null}
                {showSiteSurvey ? <col className="col-site-survey" /> : null}
                {showAuthorityColumn ? (
                  <col className="col-authority-claimed" />
                ) : null}
                <col className="col-num" />
                <col className="col-revised" />
                {showEvidenceColumn ? <col className="col-image" /> : null}
                {showSiteSurvey ? <col className="col-site-image" /> : null}
              </colgroup>
              <thead>
                <tr>
                  <th>Set</th>
                  <th>Meter</th>
                  <th className="th-multiline">
                    Serial
                    <br />
                    No.
                  </th>
                  <th>MF</th>
                  <th>Previous</th>
                  <th className="th-multiline">
                    As
                    <br />
                    Charged
                  </th>
                  <th className="th-multiline">
                    Units
                    <br />
                    Charged
                  </th>
                  {showClaimedColumn ? (
                    <th className="th-multiline">
                      User Claimed
                      <br />
                      Reading
                    </th>
                  ) : null}
                  {showSiteSurvey ? (
                    <th className="th-multiline">
                      Site Survey
                      <br />
                      Reading
                    </th>
                  ) : null}
                  {showAuthorityColumn ? (
                    <th className="th-multiline">
                      Authority
                      <br />
                      Approved Reading
                    </th>
                  ) : null}
                  <th>Units Adj.</th>
                  <th>Units Revised</th>
                  {showEvidenceColumn ? <th>Evidence</th> : null}
                  {showSiteSurvey ? (
                    <th className="th-multiline">
                      Site
                      <br />
                      Evidence
                    </th>
                  ) : null}
                </tr>
              </thead>
            </table>
          </div>
          <div className="mrc-meter-table__body">
            <table>
              <colgroup>
                <col className="col-set" />
                <col className="col-meter" />
                <col className="col-serial" />
                <col className="col-mf" />
                <col className="col-num" />
                <col className="col-num" />
                <col className="col-num" />
                {showClaimedColumn ? <col className="col-claimed" /> : null}
                {showSiteSurvey ? <col className="col-site-survey" /> : null}
                {showAuthorityColumn ? (
                  <col className="col-authority-claimed" />
                ) : null}
                <col className="col-num" />
                <col className="col-revised" />
                {showEvidenceColumn ? <col className="col-image" /> : null}
                {showSiteSurvey ? <col className="col-site-image" /> : null}
              </colgroup>
              <tbody>
              {rows.map((row, index) => {
                const isMdi = isMdiMeterRow(row);
                const flag = hideEvidenceStatus ? null : row.imageFlag;
                const hasImage = Boolean(row.imageSrc) && !isMdi;
                const statusClass = hideEvidenceStatus
                  ? "is-na"
                  : flag === 1
                    ? "is-accepted"
                    : flag === -1
                      ? "is-rejected"
                      : hasImage
                        ? "is-pending"
                        : "is-na";
                const isNewSet =
                  index === 0 || rows[index - 1].setNo !== row.setNo;
                const authorityError = authorityErrors[row.id] || "";
                const authorityRequired = isAuthorityRequiredForRow(row);
                const authorityRaw = String(
                  showAuthorityClaimed && !readOnly
                    ? authorityReadings[row.id] ?? ""
                    : row.authorityClaimed ?? authorityReadings[row.id] ?? ""
                ).trim();
                // Treat blank / "0" (API default) as no authority reading.
                // Dropdown stores claimed:123 / survey:123 — parse numeric for calcs.
                const authorityValue = (() => {
                  if (authorityRaw === "" || authorityRaw === "0") return null;
                  if (siteSurveyReadOnly) {
                    return parseAuthoritySelectReading(authorityRaw);
                  }
                  return toNumberOrNull(authorityRaw);
                })();
                const siteSurveyError = siteSurveyErrors[row.id] || "";
                const siteImageError = siteImageErrors[row.id] || "";
                const siteSurveyRequired = isSiteSurveyReadingRequired(row);
                // Prefer live input; fall back to saved document reading.
                const siteSurveyValue = toNumberOrNull(
                  siteSurveyReadings[row.id] ?? row.siteSurveyReading
                );
                const siteImageEntry = siteImages[row.id];

                const claimedPresent = hasMrcPresentReading(row.claimed);
                let unitsAdjDisplay =
                  isMdi || !claimedPresent
                    ? null
                    : calcUnitsAdjusted(row.claimed, row.asCharged, row.mf);
                let unitsRevisedDisplay =
                  isMdi || !claimedPresent
                    ? null
                    : calcUnitsChargedRevised(row.claimed, row.previous, row.mf);

                // After Site Survey → Forward for Billing:
                // Units Adj. = (Authority Approved − As Charged) × MF
                // Units Rev. = (Authority Approved − Previous) × MF
                if (!isMdi && siteSurveyReadOnly && showAuthorityClaimed) {
                  unitsAdjDisplay =
                    authorityValue == null
                      ? null
                      : calcUnitsAdjusted(
                          authorityValue,
                          row.asCharged,
                          row.mf
                        );
                  unitsRevisedDisplay =
                    authorityValue == null
                      ? null
                      : calcUnitsChargedRevised(
                          authorityValue,
                          row.previous,
                          row.mf
                        );
                } else if (!isMdi && showSiteSurvey && !siteSurveyReadOnly) {
                  // Active Site Survey stage
                  // Adj = (Site Survey − As Charged) × MF
                  // Rev = (Site Survey − Previous) × MF
                  unitsAdjDisplay =
                    siteSurveyValue == null
                      ? null
                      : calcUnitsAdjusted(
                          siteSurveyValue,
                          row.asCharged,
                          row.mf
                        );
                  unitsRevisedDisplay =
                    siteSurveyValue == null
                      ? null
                      : calcUnitsChargedRevised(
                          siteSurveyValue,
                          row.previous,
                          row.mf
                        );
                } else if (!isMdi && showAuthorityClaimed) {
                  // Authority Verification (before / without survey)
                  // Adj = (Authority − As Charged) × MF
                  // Rev = (Authority − Previous) × MF
                  unitsAdjDisplay =
                    authorityValue == null
                      ? null
                      : calcUnitsAdjusted(
                          authorityValue,
                          row.asCharged,
                          row.mf
                        );
                  unitsRevisedDisplay =
                    authorityValue == null
                      ? null
                      : calcUnitsChargedRevised(
                          authorityValue,
                          row.previous,
                          row.mf
                        );
                } else if (!isMdi && showSiteSurvey) {
                  unitsAdjDisplay =
                    siteSurveyValue == null
                      ? null
                      : calcUnitsAdjusted(
                          siteSurveyValue,
                          row.asCharged,
                          row.mf
                        );
                  unitsRevisedDisplay =
                    siteSurveyValue == null
                      ? null
                      : calcUnitsChargedRevised(
                          siteSurveyValue,
                          row.previous,
                          row.mf
                        );
                }

                return (
                  <tr
                    key={row.id}
                    className={[
                      `row-${statusClass}`,
                      isNewSet ? "row-set-start" : "",
                      isMdi ? "row-mdi" : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <td className="cell-text cell-set">
                      <span
                        className={`set-badge${isNewSet ? "" : " set-badge--muted"}`}
                      >
                        {formatDisplayValue(row.setNo)}
                      </span>
                    </td>
                    <td className="cell-text cell-meter">
                      <span
                        className="meter-type-pill"
                        title={String(row.meterLabel || "").replace(/\n/g, " ")}
                      >
                        {row.meterLabel}
                      </span>
                    </td>
                    <td className="cell-text cell-serial">
                      {formatDisplayValue(row.meterSerialNo)}
                    </td>
                    <td className="cell-text cell-mf">
                      {formatDisplayValue(row.mf)}
                    </td>
                    <td className="cell-text cell-num">
                      {formatDisplayValue(row.previous)}
                    </td>
                    <td className="cell-text cell-num">
                      {formatDisplayValue(row.asCharged)}
                    </td>
                    <td className="cell-text cell-num cell-calc">
                      {formatUnitsChargedDisplay(row.unitsCharged)}
                    </td>
                    {showClaimedColumn ? (
                      <td className="cell-text cell-num cell-claimed">
                        {isMdi ? "—" : formatDisplayValue(row.claimed)}
                      </td>
                    ) : null}
                    {showSiteSurvey ? (
                      <td
                        className={
                          siteSurveyReadOnly
                            ? "cell-text cell-num cell-claimed"
                            : "cell-site-survey"
                        }
                      >
                        {isMdi ? (
                          <span className="cell-text">—</span>
                        ) : siteSurveyReadOnly ? (
                          formatDisplayValue(
                            row.siteSurveyReading ?? siteSurveyReadings[row.id]
                          )
                        ) : (
                          <div className="mrc-meter-claimed-wrap">
                            <TextField
                              fullWidth
                              size="small"
                              value={siteSurveyReadings[row.id] ?? ""}
                              onChange={(e) =>
                                handleSiteSurveyReadingChange(
                                  row.id,
                                  e.target.value
                                )
                              }
                              placeholder={
                                siteSurveyRequired
                                  ? "Enter reading"
                                  : "Optional"
                              }
                              className="textInputField mrc-meter-input mrc-meter-input--editable"
                              error={Boolean(siteSurveyError)}
                              title={siteSurveyError || undefined}
                              required={siteSurveyRequired}
                              inputProps={{
                                inputMode: "decimal",
                                step: "0.01",
                                "aria-label": `Site survey reading set ${row.setNo}`,
                                "aria-required": siteSurveyRequired,
                                title: siteSurveyError || undefined,
                              }}
                            />
                            <div
                              className={`mrc-meter-error-slot${
                                siteSurveyError
                                  ? " mrc-meter-error-slot--active"
                                  : ""
                              }`}
                              aria-hidden="true"
                            />
                          </div>
                        )}
                      </td>
                    ) : null}
                    {showAuthorityColumn ? (
                      <td
                        className={
                          readOnly
                            ? "cell-text cell-num cell-claimed"
                            : "cell-authority-claimed"
                        }
                      >
                        {isMdi ? (
                          <span className="cell-text">—</span>
                        ) : readOnly ? (
                          formatDisplayValue(
                            row.authorityClaimed != null
                              ? row.authorityClaimed
                              : authorityReadings[row.id] || null
                          )
                        ) : siteSurveyReadOnly ? (
                          <div className="mrc-meter-claimed-wrap">
                            {(() => {
                              const selectOptions = getAuthoritySelectOptions(row);
                              return (
                                <>
                                  <TextField
                                    select
                                    fullWidth
                                    size="small"
                                    value={authorityReadings[row.id] ?? ""}
                                    onChange={(e) =>
                                      handleAuthorityClaimedChange(
                                        row.id,
                                        e.target.value
                                      )
                                    }
                                    className="textInputField mrc-meter-input mrc-meter-input--editable mrc-meter-input--select"
                                    error={Boolean(authorityError)}
                                    title={authorityError || undefined}
                                    required={authorityRequired}
                                    SelectProps={{
                                      displayEmpty: true,
                                      MenuProps: {
                                        disablePortal: false,
                                        container:
                                          typeof document !== "undefined"
                                            ? document.body
                                            : undefined,
                                        disableScrollLock: true,
                                        marginThreshold: 8,
                                        PaperProps: {
                                          className:
                                            "mrc-authority-select-menu",
                                        },
                                        sx: { zIndex: 2000 },
                                        anchorOrigin: {
                                          vertical: "bottom",
                                          horizontal: "left",
                                        },
                                        transformOrigin: {
                                          vertical: "top",
                                          horizontal: "left",
                                        },
                                      },
                                      renderValue: (selected) => {
                                        if (
                                          selected === "" ||
                                          selected == null
                                        ) {
                                          return (
                                            <span className="mrc-authority-select-placeholder">
                                              {authorityRequired
                                                ? "Select"
                                                : "Optional"}
                                            </span>
                                          );
                                        }
                                        const match = selectOptions.find(
                                          (o) => o.value === String(selected)
                                        );
                                        // Selected: show units only (menu keeps labels).
                                        if (match) return match.reading;
                                        return String(
                                          parseAuthoritySelectReading(
                                            selected
                                          ) ?? selected
                                        );
                                      },
                                    }}
                                    inputProps={{
                                      "aria-label": `Authority approved reading set ${row.setNo}`,
                                      "aria-required": authorityRequired,
                                      title: authorityError || undefined,
                                    }}
                                  >
                                    <MenuItem value="" disabled>
                                      <em>
                                        {authorityRequired
                                          ? "Select"
                                          : "Optional"}
                                      </em>
                                    </MenuItem>
                                    {selectOptions.map((opt) => (
                                      <MenuItem
                                        key={opt.value}
                                        value={opt.value}
                                        className="mrc-authority-select-option"
                                      >
                                        <span className="mrc-authority-select-option__prefix">
                                          {opt.value.startsWith("survey:")
                                            ? "Site Survey"
                                            : "User Claimed"}
                                        </span>
                                        <span className="mrc-authority-select-option__value">
                                          {opt.reading}
                                        </span>
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                  <div
                                    className={`mrc-meter-error-slot${
                                      authorityError
                                        ? " mrc-meter-error-slot--active"
                                        : ""
                                    }`}
                                    aria-hidden="true"
                                  />
                                </>
                              );
                            })()}
                          </div>
                        ) : (
                          <div className="mrc-meter-claimed-wrap">
                            <TextField
                              fullWidth
                              size="small"
                              value={authorityReadings[row.id] ?? ""}
                              onChange={(e) =>
                                handleAuthorityClaimedChange(
                                  row.id,
                                  e.target.value
                                )
                              }
                              placeholder={
                                authorityRequired
                                  ? "Enter reading"
                                  : "Optional"
                              }
                              className="textInputField mrc-meter-input mrc-meter-input--editable"
                              error={Boolean(authorityError)}
                              title={authorityError || undefined}
                              required={authorityRequired}
                              inputProps={{
                                inputMode: "decimal",
                                step: "0.01",
                                "aria-label": `Authority approved reading set ${row.setNo}`,
                                "aria-required": authorityRequired,
                                title: authorityError || undefined,
                              }}
                            />
                            <div
                              className={`mrc-meter-error-slot${
                                authorityError
                                  ? " mrc-meter-error-slot--active"
                                  : ""
                              }`}
                              aria-hidden="true"
                            />
                          </div>
                        )}
                      </td>
                    ) : null}
                    <td className="cell-text cell-num cell-calc">
                      {isMdi ? "—" : formatUnitsAdjDisplay(unitsAdjDisplay)}
                    </td>
                    <td className="cell-text cell-num cell-calc">
                      {isMdi ? "—" : formatUnitsRevisedDisplay(unitsRevisedDisplay)}
                    </td>
                    {showEvidenceColumn ? (
                      <td className="cell-image">
                        {isMdi || !row.imageSrc ? (
                          <span className="mrc-image-na">—</span>
                        ) : (
                          <div className="mrc-meter-image-wrap">
                            <button
                              type="button"
                              className={`mrc-meter-image-preview mrc-meter-image-preview--readonly${
                                evidenceViewOnly
                                  ? " mrc-meter-image-preview--clickable"
                                  : " mrc-meter-image-preview--verify"
                              } ${statusClass}`}
                              style={{
                                backgroundColor:
                                  !hideEvidenceStatus &&
                                  (flag === 1 || flag === -1)
                                    ? getDocumentBackGroundColor(flag)
                                    : undefined,
                              }}
                              onClick={() => {
                                if (evidenceViewOnly) {
                                  setPreviewSiteImage({
                                    src: row.imageSrc,
                                    name: `${row.meterLabel} — Set ${row.setNo} Evidence`,
                                    remarks: getMrcEvidenceRemarksLabel(
                                      getRemarksRow(row)
                                    ),
                                    remarksVariant:
                                      getMrcEvidenceRemarksVariant(
                                        getRemarksRow(row)
                                      ),
                                  });
                                  return;
                                }
                                setActiveRow(row);
                              }}
                              title={
                                evidenceViewOnly || hideEvidenceStatus
                                  ? "Click to view"
                                  : flag === 1
                                    ? "Accepted — click to review"
                                    : flag === -1
                                      ? "Rejected — click to review"
                                      : "Click to Accept or Reject"
                              }
                            >
                              <MrcEvidencePreview
                                src={row.imageSrc}
                                alt={`Claimed reading set ${row.setNo}`}
                              />
                              {!hideEvidenceStatus && flag === -1 ? (
                                <img
                                  className="mrc-meter-image-preview__status"
                                  src={crossIcon}
                                  alt="Rejected"
                                />
                              ) : null}
                              {!hideEvidenceStatus &&
                              !evidenceViewOnly &&
                              flag !== 1 &&
                              flag !== -1 ? (
                                <span className="mrc-meter-image-preview__cta">
                                  Review
                                </span>
                              ) : null}
                            </button>
                            {!hideEvidenceStatus ? (
                              <span
                                className={`mrc-image-status mrc-image-status--${statusClass}`}
                              >
                                {statusLabel(flag, true)}
                              </span>
                            ) : null}
                          </div>
                        )}
                      </td>
                    ) : null}
                    {showSiteSurvey ? (
                      <td className="cell-site-image">
                        {isMdi ? (
                          <span className="mrc-image-na">—</span>
                        ) : (
                          (() => {
                            const siteSrc =
                              siteImageEntry?.thumbnail ||
                              ((siteSurveyReadOnly || readOnly) &&
                                row.siteSurveyImageSrc) ||
                              "";
                            const hasSiteSrc = Boolean(siteSrc);

                            // Forward / Post (read-only): match Evidence column styling
                            if (
                              (siteSurveyReadOnly || readOnly) &&
                              hasSiteSrc
                            ) {
                              return (
                                <div className="mrc-meter-image-wrap">
                                  <button
                                    type="button"
                                    className="mrc-meter-image-preview mrc-meter-image-preview--readonly mrc-meter-image-preview--clickable is-accepted"
                                    title="Click to view"
                                    onClick={() => {
                                      setPreviewSiteImage({
                                        src: siteSrc,
                                        name: `${row.meterLabel} — Set ${row.setNo} Site Evidence`,
                                      });
                                    }}
                                  >
                                    <MrcEvidencePreview
                                      src={siteSrc}
                                      alt={`Site evidence set ${row.setNo}`}
                                    />
                                  </button>
                                  <span className="mrc-image-status mrc-image-status--is-accepted">
                                    Accepted
                                  </span>
                                </div>
                              );
                            }

                            return (
                          <div className="mrc-meter-image-wrap">
                            {hasSiteSrc ? (
                              <div
                                className="mrc-meter-image-preview mrc-meter-image-preview--clickable"
                                role="button"
                                tabIndex={0}
                                title="Click to view"
                                onClick={() => {
                                  if (!siteSrc) return;
                                  setPreviewSiteImage({
                                    src: siteSrc,
                                    name: `${row.meterLabel} — Set ${row.setNo} Site Evidence`,
                                  });
                                }}
                                onKeyDown={(e) => {
                                  if (e.key !== "Enter" && e.key !== " ") return;
                                  e.preventDefault();
                                  if (!siteSrc) return;
                                  setPreviewSiteImage({
                                    src: siteSrc,
                                    name: `${row.meterLabel} — Set ${row.setNo} Site Evidence`,
                                  });
                                }}
                              >
                                <MrcEvidencePreview
                                  src={siteSrc}
                                  alt={`Site evidence set ${row.setNo}`}
                                />
                                {!siteSurveyReadOnly ? (
                                  <button
                                    type="button"
                                    className="mrc-meter-image-preview__clear"
                                    aria-label={`Remove site evidence set ${row.setNo}`}
                                    title="Remove to reupload"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      clearSiteImage(row.id);
                                    }}
                                  >
                                    <CloseIcon fontSize="inherit" />
                                  </button>
                                ) : null}
                              </div>
                            ) : siteSurveyReadOnly ? (
                              <span className="mrc-image-na">—</span>
                            ) : (
                              <Button
                                variant="outlined"
                                component="label"
                                size="small"
                                startIcon={
                                  <FileUploadOutlinedIcon fontSize="small" />
                                }
                                className={`mrc-meter-image-btn${
                                  siteImageError
                                    ? " mrc-meter-image-btn--error"
                                    : ""
                                }`}
                                title={
                                  siteImageError || "Upload JPG, PNG, or PDF"
                                }
                              >
                                Upload
                                <input
                                  hidden
                                  accept={documentExtensions}
                                  type="file"
                                  onChange={(e) =>
                                    handleSiteImageUpload(row.id, e)
                                  }
                                />
                              </Button>
                            )}
                            {!siteSurveyReadOnly ? (
                              <div
                                className={`mrc-meter-error-slot${
                                  siteImageError
                                    ? " mrc-meter-error-slot--active"
                                    : ""
                                }`}
                                aria-hidden="true"
                              />
                            ) : null}
                          </div>
                            );
                          })()
                        )}
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
            </table>
          </div>
        </div>

        {activeRow?.imageSrc ? (
          <OpenBase64ImageInPopup
            docVerifyPopUp={Boolean(activeRow)}
            hideDocVerifyPopup={hideImagePopup}
            documentName={`${activeRow.meterLabel} — Set ${activeRow.setNo}`}
            documentImage={activeRow.imageSrc}
            width="100%"
            document={activeRow.imageSrc}
            documentFlag={getRemarksRow(activeRow)?.imageFlag}
            applicationProgressId={applicationProgressId}
            objectionStatus={objectionStatus}
            updateDocFlag={handleUpdateDocFlag}
            titleToUpdateFlag={String(activeRow.id)}
            documentRemarks={getMrcEvidenceRemarksLabel(
              getRemarksRow(activeRow)
            )}
            remarksVariant={getMrcEvidenceRemarksVariant(
              getRemarksRow(activeRow)
            )}
            flag_document={getRemarksRow(activeRow)?.imageFlag}
            verificationPermissionService="Meter Reading Correction"
            verificationPermissionNames={[
              "Application Verification",
              "Document Verification",
            ]}
          />
        ) : null}

        {previewSiteImage?.src ? (
          <OpenBase64ImageInPopup
            docVerifyPopUp={Boolean(previewSiteImage)}
            hideDocVerifyPopup={() => setPreviewSiteImage(null)}
            documentName={previewSiteImage.name || "Site Evidence"}
            documentImage={previewSiteImage.src}
            width="100%"
            document={previewSiteImage.src}
            documentRemarks={previewSiteImage.remarks || ""}
            remarksVariant={previewSiteImage.remarksVariant || ""}
            readOnly
          />
        ) : null}
      </div>
    );
  }
);

MrcDocumentsReadingTable.displayName = "MrcDocumentsReadingTable";

export default MrcDocumentsReadingTable;
